import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

const STORAGE_KEY = 'elda-map-style'
const STORAGE_KEY_3D = 'elda-map-3d'

const previewBase = (styleId: string): string =>
  `https://api.mapbox.com/styles/v1/mapbox/${styleId}/static/6.87,45.92,13/200x200@2x?logo=false&attribution=false&access_token=`

type MapStyle = {
  id: string
  label: string
  url: string
  preview: string
}

export const MAP_STYLES: MapStyle[] = [
  {
    id: 'satellite',
    label: 'Satellite',
    url: 'mapbox://styles/mapbox/satellite-v9',
    preview: previewBase('satellite-v9'),
  },
  {
    id: 'satellite-streets',
    label: 'Satellite & Rues',
    url: 'mapbox://styles/mapbox/satellite-streets-v12',
    preview: previewBase('satellite-streets-v12'),
  },
  {
    id: 'outdoors',
    label: 'Plein air',
    url: 'mapbox://styles/mapbox/outdoors-v12',
    preview: previewBase('outdoors-v12'),
  },
  {
    id: 'streets',
    label: 'Rues',
    url: 'mapbox://styles/mapbox/streets-v12',
    preview: previewBase('streets-v12'),
  },
  {
    id: 'light',
    label: 'Clair',
    url: 'mapbox://styles/mapbox/light-v11',
    preview: previewBase('light-v11'),
  },
  {
    id: 'dark',
    label: 'Sombre',
    url: 'mapbox://styles/mapbox/dark-v11',
    preview: previewBase('dark-v11'),
  },
]

const DEFAULT_STYLE_ID = 'satellite'
const DEFAULT_IS_3D = true

type MapStyleContextValue = {
  styleId: string
  styleUrl: string
  styleName: string
  previewUrl: string
  setMapStyle: (id: string) => void
  styles: MapStyle[]
  is3D: boolean
  setIs3D: (value: boolean) => void
  toggle3D: () => void
}

const MapStyleContext = createContext<MapStyleContextValue | undefined>(
  undefined,
)

export const MapStyleProvider = ({ children }: { children: ReactNode }) => {
  const [styleId, setStyleId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_STYLE_ID
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && MAP_STYLES.some(s => s.id === stored)) return stored
    return DEFAULT_STYLE_ID
  })

  const [is3D, setIs3DState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return DEFAULT_IS_3D
    const stored = localStorage.getItem(STORAGE_KEY_3D)
    if (stored === 'true' || stored === 'false') return stored === 'true'
    return DEFAULT_IS_3D
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, styleId)
  }, [styleId])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_3D, String(is3D))
  }, [is3D])

  const setMapStyle = useCallback((id: string) => {
    if (MAP_STYLES.some(s => s.id === id)) {
      setStyleId(id)
    }
  }, [])

  const setIs3D = useCallback((value: boolean) => {
    setIs3DState(Boolean(value))
  }, [])

  const toggle3D = useCallback(() => {
    setIs3DState(prev => !prev)
  }, [])

  const currentStyle = useMemo(
    () => MAP_STYLES.find(s => s.id === styleId) || MAP_STYLES[0],
    [styleId],
  )

  const value = useMemo<MapStyleContextValue>(
    () => ({
      styleId,
      styleUrl: currentStyle.url,
      styleName: currentStyle.label,
      previewUrl: currentStyle.preview,
      setMapStyle,
      styles: MAP_STYLES,
      is3D,
      setIs3D,
      toggle3D,
    }),
    [styleId, currentStyle, setMapStyle, is3D, setIs3D, toggle3D],
  )

  return (
    <MapStyleContext.Provider value={value}>
      {children}
    </MapStyleContext.Provider>
  )
}

export const useMapStyle = (): MapStyleContextValue => {
  const context = useContext(MapStyleContext)
  if (!context) {
    throw new Error('useMapStyle must be used within MapStyleProvider')
  }
  return context
}
